import { ReactElement } from 'react';

import { Instances } from '~/components/Instances';

import { AppRoute } from '../../config';

import { NotFound } from '../common';

import { Account, Billing, TeamMembers, Authentication, SingleSignOnSettings } from './Profile';
import { SignIn, SignUp, Onboarding, Recover } from './Account';
import {
  AccountActivation,
  AcceptInvitation,
  UnsubscribeActivation,
  PasswordResetActivation,
  AuthRequestActivation,
  MarketplaceActivation,
  AzureMarketplaceActivation,
} from './Activation';
import { PricingPlans, PricingSubscribe, TrialExpiredPage } from './PricingPlans';
import { Launchpad } from './Launchpad';

// Pods
import { CloudPods, CloudPodsPublic, PodDetails, CloudPodsLoad } from './CloudPods';

import {
  Quickstart,
  DemoOne,
  DemoTwo,
  DemoThree,
  DemoFour,
  DemoFive,
  QuickstartCloudFormation,
  ServerlessQuizApp,
  LocalStackSwagStore,
} from './Quickstart';
import { Stack } from './Stacks';
import { SSOCallback, SSOStart, SSOLoading } from './SSO';
import { GettingStarted } from './GettingStarted';
import { DesktopDownload } from './DesktopDownload/DesktopDownload';
import { AuthTokens } from './AuthTokens/AuthTokens';
import { ExtensionsLibrary, ExtensionsSubmit } from './Extensions';

import { WorkspaceSettings } from './Workspace';
import { UsageInsights } from './UsageInsights';

import { StateManager, ExtensionsManage, ExtensionsDetails } from './Instances';
import { StudentVerification } from './Integrations';
import { Redirect } from './Redirect';
import { AuthRelay } from './AuthRelay/AuthRelay';
import { IdentityLogs } from './IdentityLog';

type PathElementMapEntry = {
  [componentName: string]: () => ReactElement;
};

// Define the PathElementMapType type
type PathElementMapType = {
  [key: string]: PathElementMapEntry;
};

export const PATH_ELEMENT_MAP: PathElementMapType = {
  [AppRoute.SIGN_UP]: { SignUp: SignUp },
  [AppRoute.ONBOARDING]: { Onboarding: Onboarding },
  [AppRoute.SIGN_IN]: { SignIn: SignIn },
  [AppRoute.STACKS_STACK]: { Stack: Stack },
  [AppRoute.QUICKSTART]: { Quickstart: Quickstart },
  [AppRoute.QUICKSTARTCLOUDFORMATION]: { QuickstartCloudFormation: QuickstartCloudFormation },
  [AppRoute.QUICKSTART_DEMO1]: { DemoOne: DemoOne },
  [AppRoute.QUICKSTART_DEMO2]: { DemoTwo: DemoTwo },
  [AppRoute.QUICKSTART_DEMO3]: { DemoThree: DemoThree },
  [AppRoute.QUICKSTART_DEMO4]: { DemoFour: DemoFour },
  [AppRoute.QUICKSTART_DEMO5]: { DemoFive: DemoFive },
  [AppRoute.QUICKSTART_SERVERLESS_QUIZ_APP]: { ServerlessQuizApp: ServerlessQuizApp },
  [AppRoute.QUICKSTART_LOCALSTACK_SWAG_STORE]: { LocalStackSwagStore: LocalStackSwagStore },
  [AppRoute.REDIRECT]: { VsCodeExtensionRedirect: Redirect },
  [AppRoute.GETTING_STARTED]: { GettingStarted: GettingStarted },
  [AppRoute.SETTINGS_PROFILE]: { Account: Account },
  [AppRoute.SETTINGS_AUTHENTICATION]: { Authentication: Authentication },
  [AppRoute.SETTINGS_WORKSPACE]: { WorkspaceSettings: WorkspaceSettings },
  [AppRoute.SETTINGS_BILLING]: { Billing: Billing },
  [AppRoute.SETTINGS_MEMBERS]: { TeamMembers: TeamMembers },
  [AppRoute.SETTINGS_SUBSCRIPTION_UPDATE]: { PricingSubscribe: PricingSubscribe },
  [AppRoute.SETTINGS_AUTH_TOKENS]: { AuthTokens: AuthTokens },
  [AppRoute.SETTINGS_SSO]: { SingleSignOnSettings: SingleSignOnSettings },
  [AppRoute.SETTINGS_IDENTITY_LOGS]: { IdentityLogs: IdentityLogs },
  [AppRoute.SETTINGS_USAGE]: { UsageInsights: UsageInsights },
  [AppRoute.PRICING]: { PricingPlans: PricingPlans },
  [AppRoute.PRICING_SUBSCRIBE]: { PricingSubscribe: PricingSubscribe },
  [AppRoute.LAUNCHPAD]: { Launchpad: Launchpad },
  [AppRoute.DESKTOP_DOWNLOAD]: { DesktopDownload: DesktopDownload },
  [AppRoute.SSO_CALLBACK]: { SSOCallback: SSOCallback },
  [AppRoute.SSO_LOADING]: { SSOLoading: SSOLoading },
  [AppRoute.SSO_START]: { SSOStart: SSOStart },
  [AppRoute.STUDENT_VERIFICATION]: { StudentCallback: StudentVerification },
  [AppRoute.EXTENSIONS_LIBRARY]: { ExtensionsLibrary: ExtensionsLibrary },
  [AppRoute.EXTENSIONS_MANAGE]: { ExtensionsManage: ExtensionsManage },
  [AppRoute.EXTENSIONS_DETAILS]: { ExtensionsDetails: ExtensionsDetails },
  [AppRoute.EXTENSIONS_SUBMIT]: { ExtensionsSubmit: ExtensionsSubmit },
  [AppRoute.INSTANCES]: { Instances: Instances },
  [AppRoute.INSTANCES_EPHEMERAL]: { Instances: Instances },
  [AppRoute.PODS]: { CloudPods: CloudPods },
  [AppRoute.PODS_PUBLIC]: { CloudPodsPublic: CloudPodsPublic },
  [AppRoute.POD]: { PodDetails: PodDetails },
  [AppRoute.POD_DIFF]: { PodDetails: PodDetails },
  [AppRoute.POD_LOAD]: { CloudPodsLoad: CloudPodsLoad },
  [AppRoute.ACCOUNT_ACTIVATE]: { AccountActivation: AccountActivation },
  [AppRoute.ACCEPT_INVITATION]: { AcceptInvitation: AcceptInvitation },
  [AppRoute.UNSUBSCRIBE_ACTIVATE]: { UnsubscribeActivation: UnsubscribeActivation },
  [AppRoute.RECOVER]: { Recover: Recover },
  [AppRoute.RECOVER_ACTIVATE]: { PasswordResetActivation: PasswordResetActivation },
  [AppRoute.AUTH_REQUEST_ACTIVATE]: { AuthRequestActivation: AuthRequestActivation },
  [AppRoute.MARKETPLACE_ACTIVATE]: { MarketplaceActivation: MarketplaceActivation },
  [AppRoute.AZURE_MARKETPLACE_ACTIVATE]: { AzureMarketplaceActivation: AzureMarketplaceActivation },
  [AppRoute.NOT_FOUND]: { NotFound: NotFound },
  [AppRoute.AUTH_RELAY]: { AuthRelay: AuthRelay },
  [AppRoute.STACKS_OVERVIEW]: { Stack: Stack },
  [AppRoute.INSTANCE_STATE]: { StateManager: StateManager },
  [AppRoute.PRICING_TRIAL_EXPIRED]: { TrialExpiredPage: TrialExpiredPage },
};
